import { useEffect, useRef, useState } from 'react';
import {
  downloadFlightAttachment,
  downloadIssueAttachment,
  getFlightAttachmentFile,
  getIssueAttachmentFile,
} from '../../api';
import { Flight, FlightAttachment, IssueAttachment } from '../../types';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  blobWithMime,
  previewKindFromName,
  type PreviewKind,
} from '../../utils/filePreview';
import { flightDetailRows } from '../../utils/flightDisplay';
import { useFeedback } from '../../hooks/useFeedback';
import DocumentPreviewModal from '../DocumentPreviewModal/DocumentPreviewModal';
import PilotBadge from '../PilotBadge/PilotBadge';
import RecordDetailsModal from '../RecordDetailsModal/RecordDetailsModal';
import '../IssueAttachmentsBlock/IssueAttachmentsBlock.css';

type PreviewItem = {
  key: string;
  name: string;
  title: string;
  load: () => Promise<Blob>;
  download: () => Promise<void>;
};

type Props = {
  flight: Flight;
  canEdit?: boolean;
  canAdmin?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onSplit: () => void;
  onDelete: () => void;
  onOpenIssue?: () => void;
};

const asPreviewItems = (flight: Flight): PreviewItem[] => {
  const tach = (flight.attachments || []).map((file: FlightAttachment) => ({
    key: `tach-${file.id}`,
    name: file.original_name || file.filename,
    title: 'Фото тахометра',
    load: () =>
      getFlightAttachmentFile(file.flight_id, file.id).then((res) => res.data),
    download: () =>
      downloadFlightAttachment(
        file.flight_id,
        file.id,
        file.original_name || file.filename
      ),
  }));
  const issueFiles = (flight.issues || []).flatMap((issue) =>
    (issue.attachments || []).map((file: IssueAttachment) => ({
      key: `issue-${file.id}`,
      name: file.original_name || file.filename,
      title: 'Файлы неисправности',
      load: () =>
        getIssueAttachmentFile(file.issue_id, file.id).then((res) => res.data),
      download: () =>
        downloadIssueAttachment(
          file.issue_id,
          file.id,
          file.original_name || file.filename
        ),
    }))
  );
  return [...tach, ...issueFiles];
};

const FlightDetailsModal = ({
  flight,
  canEdit = false,
  canAdmin = false,
  onClose,
  onEdit,
  onSplit,
  onDelete,
  onOpenIssue,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const files = asPreviewItems(flight);
  const tachFiles = files.filter((item) => item.key.startsWith('tach-'));
  const issuePreviewFiles = files.filter((item) => item.key.startsWith('issue-'));
  const [preview, setPreview] = useState<PreviewItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('other');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewRequest = useRef(0);

  const closePreview = () => {
    previewRequest.current += 1;
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const openPreview = async (file: PreviewItem) => {
    const requestId = previewRequest.current + 1;
    previewRequest.current = requestId;
    setPreview(file);
    setPreviewKind(previewKindFromName(file.name));
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    try {
      const blob = blobWithMime(await file.load(), file.name);
      if (previewRequest.current !== requestId) return;
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (error) {
      if (previewRequest.current !== requestId) return;
      setPreviewError(getApiErrorMessage(error, 'Не удалось открыть файл'));
    }
    if (previewRequest.current === requestId) setPreviewLoading(false);
  };

  const previewList = preview?.key.startsWith('issue-')
    ? issuePreviewFiles
    : tachFiles;
  const previewIndex = preview
    ? previewList.findIndex((item) => item.key === preview.key)
    : -1;

  const actions = [
    ...(canEdit ? [{ label: 'Изменить', onClick: onEdit }] : []),
    ...(canAdmin
      ? [
          { label: 'Разделить', onClick: onSplit },
          { label: 'Удалить', onClick: onDelete, danger: true },
        ]
      : []),
  ];

  const issueText =
    flight.issues && flight.issues.length > 0
      ? flight.issues.map((item) => item.description).join('; ')
      : flight.issue;

  return (
    <>
      {feedbackNode}
      <RecordDetailsModal
        title="Полёт"
        summary={
          <>
            <span className="record-details-date">{DateFormat(flight.date)}</span>
            <PilotBadge name={flight.pilot} color={flight.pilot_color} />
          </>
        }
        rows={[
          ...flightDetailRows(flight).map((row) => ({
            label: row.label,
            value: row.value,
          })),
          {
            label: 'Неисправность',
            value:
              issueText && onOpenIssue ? (
                <button
                  type="button"
                  className="table-link"
                  onClick={onOpenIssue}
                >
                  {issueText}
                </button>
              ) : (
                issueText || '—'
              ),
          },
        ]}
        note={flight.comment}
        extra={
          tachFiles.length > 0 || issuePreviewFiles.length > 0 ? (
            <div className="record-details-files">
              {tachFiles.length > 0 ? (
                <button
                  type="button"
                  className="record-details-file-open"
                  onClick={() => void openPreview(tachFiles[0])}
                >
                  Фото тахометра
                </button>
              ) : null}
              {issuePreviewFiles.length > 0 ? (
                <button
                  type="button"
                  className="record-details-file-open"
                  onClick={() => void openPreview(issuePreviewFiles[0])}
                >
                  Файлы неисправности
                </button>
              ) : null}
            </div>
          ) : null
        }
        actions={actions}
        onClose={onClose}
      />
      {preview ? (
        <DocumentPreviewModal
          title={preview.title}
          objectUrl={previewUrl}
          kind={previewKind}
          loading={previewLoading}
          error={previewError}
          fileIndex={Math.max(0, previewIndex)}
          fileCount={previewList.length}
          onPrev={() => {
            const file = previewList[previewIndex - 1];
            if (file) void openPreview(file);
          }}
          onNext={() => {
            const file = previewList[previewIndex + 1];
            if (file) void openPreview(file);
          }}
          onDownload={() => {
            void preview.download().catch((error) =>
              showError(getApiErrorMessage(error, 'Не удалось скачать файл'))
            );
          }}
          onClose={closePreview}
        />
      ) : null}
    </>
  );
};

export default FlightDetailsModal;
